// Step02 keyword strategy workbook export.
//
// The browser deliberately emits Excel 2003 XML instead of introducing a
// heavyweight spreadsheet dependency. Excel opens it as a multi-sheet
// workbook, while the strategy itself remains entirely server-owned.

const EXCEL_XML_NS = 'urn:schemas-microsoft-com:office:spreadsheet';

const TAXONOMY_LABELS = {
  category_term: '本品类目词',
  audience_term: '人群词',
  gift_term: '礼品词',
  system_recommended_term: '系统推荐词',
  structure_feature_term: '结构特征词',
  function_use_term: '功能用途词',
  spec_attribute_term: '参数属性词',
  color_material_term: '颜色/面料词',
  external_traffic_term: '外部流量词',
};

const NEGATIVE_LABELS = {
  product_mismatch: '与商品不匹配',
  deferred_profitability: '高竞价低效，暂缓投放',
  compliance_or_ip: '敏感、违规或侵权风险',
};

const PPC_STAGE_LABELS = {
  new_product: '第一阶段：新品期',
  stabilization: '第二阶段：稳定期',
  mature: '第三阶段：成熟期',
  deferred: '暂缓投放',
  blocked: '禁止投放',
};

const PLACEMENT_LABELS = {
  frontend_core: '前台核心词',
  frontend_support: '前台辅助词',
  backend_candidate: '后台 Search Terms 候选',
  ppc_only: '仅广告测试',
  blocked: '禁止使用',
  unclassified: '待语义复核',
};

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function safeCell(value) {
  const text = String(value ?? '');
  // Prevent spreadsheet formula execution if a source keyword starts with one.
  return /^[=+\-@]/.test(text) ? `'${text}` : text;
}

function escapeXml(value) {
  return safeCell(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function cell(value) {
  return `<Cell><Data ss:Type="String">${escapeXml(value)}</Data></Cell>`;
}

function worksheet(name, headers, rows) {
  const head = `<Row>${headers.map(cell).join('')}</Row>`;
  const body = rows.map((row) => `<Row>${row.map(cell).join('')}</Row>`).join('');
  return `<Worksheet ss:Name="${escapeXml(name)}"><Table>${head}${body}</Table></Worksheet>`;
}

function formatMetric(value) {
  if (value === null || value === undefined || value === '') return '未提供';
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return String(value);
}

function joinedLabels(values, labels = {}) {
  return asArray(values).map((value) => labels[value] || value).join('；');
}

function termRows(strategy) {
  return asArray(strategy?.terms).map((term) => [
    term.keyword,
    term.root,
    joinedLabels(term.taxonomy, TAXONOMY_LABELS) || '待语义复核',
    term.taxonomyStatus === 'provided' ? '已提供' : '待语义复核',
    joinedLabels(term.labels),
    formatMetric(term.metrics?.searches30d),
    formatMetric(term.metrics?.purchaseRate),
    formatMetric(term.metrics?.competition),
    formatMetric(term.metrics?.relevanceScore),
    formatMetric(term.metrics?.clickThroughRate),
    formatMetric(term.metrics?.attributedConversionRate24h),
    joinedLabels(Object.entries(term.bands || {}).map(([key, band]) => `${key}:${band}`)),
    PLACEMENT_LABELS[term.listingPlacement] || term.listingPlacement,
    PPC_STAGE_LABELS[term.ppcStage] || term.ppcStage,
    term.complianceOrIpRisk ? '是' : '否',
    term.productMismatchRisk ? '是' : '否',
  ]);
}

/**
 * Creates an Excel-compatible multi-sheet workbook for the operating SOP.
 * This is intentionally a pure function so strategy data can be tested
 * without a browser download implementation.
 */
export function buildKeywordStrategyWorkbookXml(strategy = {}) {
  const sheets = [
    worksheet('关键词总库', [
      '关键词', '词根', '分类标签', '分类状态', '策略标签', '搜索量', '购买转化率', '竞争度', '相关性', '点击率', 'AC转化率', '指标分档', 'Listing 分配', 'PPC 阶段', 'IP/合规风险', '商品不匹配',
    ], termRows(strategy)),
    worksheet('词根词库', ['词根', '关键词数', '关键词'], asArray(strategy?.rootLibrary).map((item) => [
      item.root,
      item.count,
      asArray(item.keywords).join('；'),
    ])),
    worksheet('否定词库', ['关键词', '否定类型', '广告动作', '原因'], asArray(strategy?.negativeLibrary).map((item) => [
      item.term,
      NEGATIVE_LABELS[item.type] || item.type,
      item.action,
      item.reason,
    ])),
    worksheet('主推词库', ['关键词', '词根', '策略标签', 'Listing 分配', 'PPC 阶段', '搜索量', '购买转化率', '点击率', 'AC转化率'], asArray(strategy?.primaryKeywords).map((item) => [
      item.keyword,
      item.root,
      joinedLabels(item.labels),
      PLACEMENT_LABELS[item.listingPlacement] || item.listingPlacement,
      PPC_STAGE_LABELS[item.ppcStage] || item.ppcStage,
      formatMetric(item.metrics?.searches30d),
      formatMetric(item.metrics?.purchaseRate),
      formatMetric(item.metrics?.clickThroughRate),
      formatMetric(item.metrics?.attributedConversionRate24h),
    ])),
    worksheet('PPC分阶段', ['阶段', '关键词', '词根', '策略标签', 'Listing 分配'], Object.entries(strategy?.ppcKeywordPlan || {})
      .flatMap(([stage, items]) => asArray(items).map((item) => [
        PPC_STAGE_LABELS[stage] || stage,
        item.keyword,
        item.root,
        joinedLabels(item.labels),
        PLACEMENT_LABELS[item.listingPlacement] || item.listingPlacement,
      ]))),
    worksheet('指标分档', ['维度', '数据状态', '有效样本', '最小值', '平均值', '最大值', '低档上限', '高档下限'], Object.entries(strategy?.metricProfiles || {})
      .map(([metric, profile]) => [
        metric,
        profile?.status === 'observed' ? '已观测' : '未提供',
        profile?.observedCount || 0,
        formatMetric(profile?.min),
        formatMetric(profile?.average),
        formatMetric(profile?.max),
        formatMetric(profile?.lowUpper),
        formatMetric(profile?.highLower),
      ])),
  ];

  return [
    '<?xml version="1.0"?>',
    '<?mso-application progid="Excel.Sheet"?>',
    `<Workbook xmlns="${EXCEL_XML_NS}" xmlns:ss="${EXCEL_XML_NS}">`,
    ...sheets,
    '</Workbook>',
  ].join('');
}

export function downloadKeywordStrategyWorkbook(strategy, filename = 'keyword-strategy-workbook.xls') {
  const blob = new Blob([buildKeywordStrategyWorkbookXml(strategy)], {
    type: 'application/vnd.ms-excel;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
